// T044: Create login form component with T046: form validation

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useAuthStore } from '@/stores/authStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { isValidUsername, isValidPrivateKey } from '@/utils/validators'

const loginSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(16, 'Username must be at most 16 characters')
    .refine(isValidUsername, 'Invalid username format'),
  postingKey: z
    .string()
    .min(1, 'Private key is required')
    .refine(isValidPrivateKey, 'Invalid private key format'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginForm() {
  const [showKey, setShowKey] = useState(false)
  const { login, isLoading, error } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginFormData) => {
    await login(data.username, data.postingKey)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="username" className="text-sm font-medium">
          Username
        </label>
        <Input
          id="username"
          type="text"
          placeholder="your-username"
          autoComplete="username"
          {...register('username')}
          disabled={isLoading}
        />
        {errors.username && (
          <p className="text-sm text-destructive">{errors.username.message}</p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="postingKey" className="text-sm font-medium">
          Posting Private Key
        </label>
        <div className="relative">
          <Input
            id="postingKey"
            type={showKey ? 'text' : 'password'}
            placeholder="5..."
            autoComplete="current-password"
            {...register('postingKey')}
            disabled={isLoading}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
            onClick={() => setShowKey(!showKey)}
            disabled={isLoading}
          >
            {showKey ? 'Hide' : 'Show'}
          </Button>
        </div>
        {errors.postingKey && (
          <p className="text-sm text-destructive">{errors.postingKey.message}</p>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? 'Signing in...' : 'Sign in'}
      </Button>

      <p className="text-xs text-muted-foreground">
        Don't have an account?{' '}
        <a
          href="https://steemit.com/signup"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Sign up on Steemit
        </a>
      </p>
    </form>
  )
}
